import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useMember } from "@/context/MemberAuthContext";
import { LeafLogo } from "@/components/common/LeafLogo";
import { Button } from "@/components/ui/button";
import { Globe, Phone, MapPin, Menu, X, LogIn, MessageSquareWarning, LayoutDashboard } from "lucide-react";
import { GMOAA } from "@/constants/testIds";

const fallbackNav = [
  { to: "/", key: "navHome", tid: GMOAA.navHome, end: true },
  { to: "/about", key: "navAbout", tid: GMOAA.navAbout },
  { to: "/office-bearers", key: "navBearers", tid: GMOAA.navBearers },
  { to: "/districts", key: "navDistricts", tid: GMOAA.navDistricts },
  { to: "/gr-notices", key: "navGr", tid: GMOAA.navGr },
  { to: "/gapl", key: "navGapl", tid: GMOAA.navGapl },
  { to: "/advertise", key: "navAdvertise", tid: GMOAA.navAdvertise },
  { to: "/contact", key: "navContact", tid: GMOAA.navContact },
];

export const Header = () => {
  const { t, toggle, lang, site } = useLang();
  const { isMember } = useMember();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const info = site?.site_info || {};
  const assocShort = (lang === "gu" ? info.assoc_short_gu : info.assoc_short_en) || t("assocShort");
  const assocName = (lang === "gu" ? info.assoc_name_gu : info.assoc_name_en) || t("assocName");
  const address = (lang === "gu" ? info.address_gu : info.address_en) || t("address");
  const showAddress = info.show_address !== false;
  const navItems = (site?.nav_main && site.nav_main.length)
    ? site.nav_main.map((n, i) => ({ to: n.to, label: lang === "gu" ? n.label_gu : n.label_en, end: n.to === "/", tid: `nav-item-${i}` }))
    : fallbackNav.map((n) => ({ to: n.to, label: t(n.key), end: n.end, tid: n.tid }));

  return (
    <>
      {/* Utility strip */}
      <div className="bg-gmoaa-green text-white">
        <div className="gmoaa-container flex items-center justify-between py-1.5 text-xs">
          <div className="flex items-center gap-2 truncate">
            {showAddress && <><MapPin size={13} className="shrink-0 text-gmoaa-gold" /><span className="truncate">{address}</span></>}
          </div>
          <div className="flex items-center gap-4">
            <a href="/contact" data-testid={GMOAA.helpLink} className="hidden items-center gap-1 hover:text-gmoaa-gold sm:flex">
              <Phone size={13} /> {t("helpLine")}
            </a>
            <button
              data-testid={GMOAA.langToggle}
              onClick={toggle}
              className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 font-medium transition-colors hover:bg-white/20"
            >
              <Globe size={13} /> {t("langLabel")}
            </button>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-50 border-b border-gmoaa-border-subtle bg-gmoaa-cream/90 backdrop-blur-md">
        <div className="gmoaa-container flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3">
            <LeafLogo size={44} />
            <div className="leading-tight">
              <p className="font-serif text-xl font-bold text-gmoaa-green">{assocShort}</p>
              <p className="hidden max-w-[260px] text-[11px] text-gmoaa-text-muted sm:block">{assocName}</p>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-gmoaa-green-light text-gmoaa-green" : "text-gmoaa-text-dark hover:bg-gmoaa-green-light/60"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              data-testid={GMOAA.raiseIssueBtn}
              variant="outline"
              onClick={() => navigate("/contact")}
              className="hidden border-gmoaa-saffron text-gmoaa-saffron-hover hover:bg-gmoaa-saffron-light md:inline-flex"
            >
              <MessageSquareWarning size={16} className="mr-1" /> {t("raiseIssue")}
            </Button>
            <Button
              data-testid={GMOAA.memberLoginBtn}
              onClick={() => navigate(isMember ? "/member" : "/member/login")}
              className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"
            >
              {isMember ? <LayoutDashboard size={16} className="mr-1" /> : <LogIn size={16} className="mr-1" />}
              <span className="hidden sm:inline">{isMember ? t("myDashboard") : t("memberLogin")}</span>
            </Button>
            <button
              data-testid={GMOAA.mobileMenuBtn}
              onClick={() => setMobileOpen((o) => !o)}
              className="rounded-lg p-2 text-gmoaa-green lg:hidden"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <nav className="border-t border-gmoaa-border-subtle bg-gmoaa-cream px-4 py-3 lg:hidden">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-gmoaa-green-light text-gmoaa-green" : "text-gmoaa-text-dark"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
    </>
  );
};
